import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
/**
 * Con qué cuenta manda mails cada medio. Lo administra la **plataforma** desde el backoffice, no el
 * medio desde su panel: la key la genera el cliente en su propio Resend y la carga quien lo da de
 * alta.
 *
 * **La API key no aparece en ningún DTO de lectura**, ni recortada. El único dato que se expone es
 * si hay una cargada (`configurado`). No es paranoia: no hay ninguna pantalla que necesite mostrarla
 * y un DTO que la trajera terminaría cacheado o logueado en algún lado.
 *
 * Ojo: esto no vive en `SiteBranding`. Esa forma viaja en `GET /public/site` —anónimo— y embebida en
 * el cascarón de toda respuesta pública, así que nada de acá puede mudarse ahí.
 */
export const EMAIL_REMITENTE_NOMBRE_MAX = 120;
/** Lo que se puede leer de la configuración de un medio. Sin la key. */
export const EmailDelMedioDTO = z
    .object({
    configurado: z.boolean(),
    /** `null` cuando el medio no tiene configuración propia y manda por el transporte del sistema. */
    remitenteNombre: z.string().nullable(),
    remitenteDireccion: z.string().nullable(),
    actualizadoEn: z.string().datetime().nullable(),
})
    .strict();
/**
 * Guarda la configuración.
 *
 * `apiKey` es opcional **en la edición**: ausente significa "dejá la que está". Es lo que permite
 * corregir el remitente sin tener que pedirle al cliente la key de nuevo, porque una vez guardada no
 * hay forma de leerla para volver a mandarla. El servidor exige que venga en el primer guardado.
 */
export const GuardarEmailDelMedioRequest = z
    .object({
    remitenteNombre: z.string().trim().min(1).max(EMAIL_REMITENTE_NOMBRE_MAX),
    remitenteDireccion: z.string().trim().toLowerCase().email(),
    apiKey: z.string().trim().min(1).optional(),
})
    .strict();
/**
 * Resultado de un envío de prueba. **No es un error HTTP**: que Resend rechace el envío es
 * información que el operador tiene que poder leer en la pantalla —un dominio sin verificar
 * responde 403—, no algo que haya que ir a buscar a los logs del servidor.
 */
export const ProbarEmailResponse = z
    .object({
    ok: z.boolean(),
    /** A qué casilla se mandó: la de quien apretó el botón. */
    enviadoA: z.string().email(),
    /** El mensaje crudo del proveedor cuando falló. */
    error: z.string().nullable(),
})
    .strict();
/** Un medio, como lo lista el backoffice para elegir a cuál configurarle el email. */
export const MedioDTO = z
    .object({
    id: UuidSchema,
    nombre: z.string(),
    slug: z.string(),
    domain: z.string().nullable(),
    activo: z.boolean(),
    /** Si tiene su propia cuenta de email cargada. */
    emailConfigurado: z.boolean(),
})
    .strict();
export const EMAIL_MEDIO_ERROR_CODES = {
    medioNoEncontrado: 'MEDIO_NO_ENCONTRADO',
    /** Primer guardado sin `apiKey`: no hay ninguna anterior que conservar. */
    faltaApiKey: 'EMAIL_MEDIO_FALTA_API_KEY',
    /** Se pidió probar o borrar y el medio no tiene configuración propia. */
    sinConfiguracion: 'EMAIL_MEDIO_SIN_CONFIGURACION',
};
